/* eslint-disable react/no-unescaped-entities */
/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable @typescript-eslint/no-shadow */

import { useDepot } from '#~/depot/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
// Leaf imports, NOT '#~/onboarding/index.js': index re-exports the gate, whose
// import graph reaches a Vite `?worker` module jest cannot link, so routing
// through it would make this panel untestable. See onboarding/store.ts.
import { isFunnelEnabled, isFunnelSupportedSurface } from '#~/onboarding/gate-decisions.js';
import { useOnboardingRestart } from '#~/onboarding/store.js';
import {
  getBillingLimitsState,
  limitCanAddCamera,
  useBillingLimitsState
} from '#~/api/billing/limits.js';
import { CheckingPlan, LimitExceeded } from '#~/video/components/add/source/footer.js';
import { PostHogFeatureFlag } from '#~/posthog/constants.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import { Button } from '@studio/design-system/button';
import { Footer } from '@studio/design-system/dialog';
import { Group } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import { Prompt } from '@studio/design-system/prompt';
import { normalizeId } from '@studio/models';
import type { Room as M_Room } from '@studio/models/room';
import { useLatestFn } from '@studio/react-utils/hooks';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { ActionBox, ButtonBox, Heading } from '../layout/index.js';
import { useStore } from '../store/hooks.js';
import { Panel } from '../types.js';
import { useWiring } from '../wiring.js';
import type { Props } from './types.js';

/** `<Profile>` panel. */
export const Studio = ({ setFooter, ...props }: Props): ReactElement => {
  const [dirtyKeys, setDirtyKeys] = useState<string[]>([]);
  const wiring = useWiring();

  const [rooms, produce] = useDepot((µ) => [µ.roomRecords, µ.produce], shallow);

  const [lastRoomID, _] = useLastRoomIdPref();
  const studioRoomID = normalizeId<M_Room['id']>(lastRoomID);

  const [roomId] = useStore(
    (µ) => [µ[Panel.Studio].overrideId ?? studioRoomID, µ.produce],
    shallow
  );

  const room = roomId ? rooms[roomId] : undefined;

  const [name, setName] = useState(room?.name ?? '');
  const prevRoomIdRef = useRef<T_Maybe<string>>(undefined);

  useEffect(() => {
    // Sync when roomId changes (switching rooms) or when room data first loads
    if (roomId !== prevRoomIdRef.current) {
      // Room ID changed - reset to new room's name
      setName(room?.name ?? '');
      setDirtyKeys([]); // Clear dirty keys when switching rooms
      prevRoomIdRef.current = roomId;
    } else if (room?.name && !name) {
      // Same room ID, but room data just loaded and name is still empty
      setName(room.name);
    }
  }, [roomId, room?.name]);

  const [isShowingDeleteStudioPrompt, setIsShowingDeleteStudioPrompt] = useState(false);
  const [deleteStudioPromptInputText, setDeleteStudioPromptInputText] = useState('');
  const deleteKey = `Delete ${room?.name ?? ''}`;

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleCommit = useLatestFn(async (): Promise<void> => {
    setIsSaving(true);

    await wiring.handleUpdateRoom({
      id: roomId!,
      name
    });

    produce((draft) => {
      draft.roomRecords[roomId!] = {
        ...draft.roomRecords[roomId!]!,
        id: roomId!,
        name
      };

      draft.rooms[roomId!] = {
        ...draft.rooms[roomId!]!,
        name
      };
    });

    setDirtyKeys([]);
    setIsSaving(false);
  });

  const handleDirtyCheck =
    (key: string, current: T_Maybe<string>, setter: (next: string) => void) =>
    (next: string): void => {
      setter(next);

      if (next === current) {
        setDirtyKeys((keys: string[]) => keys.filter((k) => k !== key));

        return;
      }

      setDirtyKeys((keys: string[]) => {
        if (keys.includes(key)) {
          return keys;
        }

        return [...keys, key];
      });
    };

  const handleDeleteStudio = useLatestFn((e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();

    setDeleteStudioPromptInputText('');
    setIsShowingDeleteStudioPrompt(true);
  });

  const handleConfirmDeleteStudio = useLatestFn((): void => {
    if (deleteStudioPromptInputText !== deleteKey) {
      return;
    }

    setIsDeleting(true);
    wiring.handleDeleteRoom({ id: roomId! });

    setIsShowingDeleteStudioPrompt(false);
  });

  const handleLeaveStudio = useLatestFn((e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();

    setIsLeaving(true);
    wiring.handleLeaveRoom({ id: roomId! });
  });

  // ENG-3796 re-entry (original spec section 5: "Settings -> Show onboarding
  // re-runs the wizard on demand"). Hidden unless the funnel is switched on for
  // this environment: with the flag off the gate renders no screen, so the
  // button would be a control that does nothing. isFunnelEnabled is the SAME
  // fail-closed reading the gate uses — absent, false, unreachable and still
  // loading all mean off.
  const funnelFlag = useFeatureFlag(PostHogFeatureFlag.ONBOARDING_FUNNEL, {
    defaultValue: false
  });
  // Hidden in the browser for the same reason it is hidden with the flag off:
  // the gate renders nothing there, so the button would be a control that does
  // nothing. Settings is reachable in the browser, and isFunnelSupportedSurface
  // is the same answer the gate reads, so the two cannot drift.
  const canRestartOnboarding =
    isFunnelSupportedSurface({ isElectron: isElectronApp() }) && isFunnelEnabled(funnelFlag);
  const requestRestart = useOnboardingRestart((µ) => µ.requestRestart);

  // Never launch a flow that cannot succeed (ENG-4649).
  //
  // The funnel exists to walk someone to their first camera. A studio already
  // at its plan's source limit has nothing it can do for them, so the button
  // reports the limit in place of opening four screens that end in a refusal.
  //
  // Tri-state, the same three answers the add-source footer renders and for the
  // same reason (ENG-4165/ENG-4187): while the billing read is loading or has
  // failed, say so and let the flow proceed. A slow read must never be shown to
  // a paying customer as "you are capped".
  const billingLimitsState = useBillingLimitsState();
  const isAtSourceLimit = billingLimitsState.status === 'loaded' && !limitCanAddCamera();

  // Closing the dialog is part of the action, not a courtesy: the funnel opens
  // over the app, and leaving Settings up would put it behind this panel.
  const handleRestartOnboarding = useLatestFn((): void => {
    // A camera added after the last render still cannot reopen the capped funnel.
    if (getBillingLimitsState().status === 'loaded' && !limitCanAddCamera()) return;

    requestRestart();
    wiring.handleClose();
  });

  useEffect(() => {
    setFooter(
      <Footer>
        <ButtonBox className='flex flex-1'>
          <ButtonBox.Cancel onClick={wiring.handleClose} />
          <div className='flex-1' />
          <ButtonBox.Save
            disabled={dirtyKeys.length === 0 || isSaving}
            label={isSaving ? 'Saving...' : 'Save Changes'}
            onClick={handleCommit}
          />
        </ButtonBox>
      </Footer>
    );
  }, [dirtyKeys, handleCommit, isSaving, setFooter, wiring]);

  const canUpdate = room?.permissions.canUpdate ?? false;
  const canDestroy = room?.permissions.canDestroy ?? false;
  const canLeave =
    room?.permissions.role !== AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER;

  const studioCount = Object.keys(rooms).length;

  // Check if we're still loading room data
  const isLoadingRoom = !!studioRoomID && !room;

  return (
    <div {...props} className={twMerge(props.className, 'flex h-full flex-col')}>
      <ActionBox>
        <Heading subtitle='Manage your Studio details' title='Studio Settings' />
      </ActionBox>

      <div className='my-16 flex w-[50%] flex-col gap-32'>
        <Group label='Studio Name'>
          <Input
            disabled={!canUpdate || isLoadingRoom}
            onChange={handleDirtyCheck('name', room?.name ?? '', setName)}
            placeholder={isLoadingRoom ? 'Loading studio...' : 'Studio Name'}
            value={name}
          />
        </Group>

        <hr />

        {!!canDestroy && studioCount > 1 && (
          <a className={twMerge(isDeleting && 'text-muted')} href='#' onClick={handleDeleteStudio}>
            {isDeleting ? 'Deleting' : 'Delete'} Studio
          </a>
        )}

        {!!canLeave && (
          <a className={twMerge(isLeaving && 'text-muted')} href='#' onClick={handleLeaveStudio}>
            {isLeaving ? 'Leaving' : 'Leave'} Studio
          </a>
        )}

        {/* Keyboard-reachable design-system Button rather than the raw anchors
            above, per `ui-architecture.md`. */}
        {!!canRestartOnboarding && (
          <Group label='First-run setup'>
            {/* Sized to its label. Group stretches its child to the panel's
                half-width column, which made this read as a primary action
                spanning the dialog. */}
            <div className='flex flex-col gap-8'>
              <div className='flex'>
                <Button
                  {...STagNew(Sel.AccountSettings, 'restart-onboarding')}
                  disabled={isAtSourceLimit}
                  onClick={handleRestartOnboarding}
                  size='md'
                >
                  Restart onboarding
                </Button>
              </div>
              {/* The existing callouts, not new copy: LimitExceeded already
                  carries the upgrade click-through for an admin or owner. */}
              {billingLimitsState.status !== 'loaded' ? (
                <CheckingPlan status={billingLimitsState.status} />
              ) : (
                !!isAtSourceLimit && <LimitExceeded />
              )}
            </div>
          </Group>
        )}

        {!!isShowingDeleteStudioPrompt && (
          <Prompt
            className='min-w-[450px]'
            message={
              <div className='text-[14px] font-[400] text-secondary'>
                <p>Are you sure you want to delete this studio?</p>
                <p className='mb-24'>
                  Type <strong>{deleteKey}</strong> to confirm.
                </p>
                <Input
                  className={twMerge(
                    deleteStudioPromptInputText !== deleteKey && 'border border-error'
                  )}
                  focusOnMount
                  onChange={(v): void => {
                    setDeleteStudioPromptInputText(v);
                  }}
                  type='text'
                  value={deleteStudioPromptInputText}
                />
                <Button
                  className='mx-auto mt-40 bg-error'
                  disabled={deleteStudioPromptInputText !== deleteKey}
                  onClick={handleConfirmDeleteStudio}
                  size='lg'
                >
                  Delete Studio
                </Button>
              </div>
            }
            onOpenChange={(next): void => {
              if (!next) {
                setIsShowingDeleteStudioPrompt(false);
              }
            }}
            title={
              <h1 className='mb-0 text-[24px] font-demibold'>Delete "{room?.name ?? ''}" Studio</h1>
            }
          />
        )}
      </div>
    </div>
  );
};
